/**
 * B-24b WS-3 — SA Operator Console. The real `/admin` front door.
 *
 * Promotes Mock A "Operator Console" chassis to production. Wired to WS-2
 * endpoints via useSaDashboard hooks. WS-2 is in flight; tests mock at the
 * apiClient level (no MSW) per the existing dashboardApi test pattern.
 *
 * Layout (Mock A density register; HoD-teardown rework 2026-06-08):
 *   1. Page title row (title + "Health" nav link)
 *   2. 4-cell KPI strip (Active customers · ARR · AI approval rate ·
 *      Needs-attention [red on nonzero])
 *   3. Status banner (worst platform-health signal echoed from PlatformHealth)
 *   4. Two-band operator grid that bottoms near the fold:
 *      - Band 1 (daily work): Attention (5) + AI quality (7), equal height
 *      - Band 2 (portfolio): Usage / Account health / Integrations, 3-up equal
 *   5. Per-tenant drill overlay (OperatorDrillOverlay)
 *   6. ⌘K command palette — pinned bottom-right affordance, out of grid flow
 *
 * Reconciliation:
 *   - Dashboard.tsx  → THIS FILE (the front door)
 *   - Stats.tsx      → DELETED 2026-06-08 (graveyard of the killed self-serve
 *                      motion; founder ruling)
 *   - PlatformHealth → stays its own page at /platform-health; worst signal
 *                      echoed via OperatorStatusBanner
 *
 * Dark + light parity: semantic tokens throughout. Desktop-only SA surface
 * (no mobile breakpoints required).
 */
import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  useSaCalibrationHealth,
  useSaAccountHealth,
} from "@/hooks/queries/admin/useSaDashboard";
import { OperatorKpiStrip } from "@/components/admin/dashboard/OperatorKpiStrip";
import { OperatorStatusBanner } from "@/components/admin/dashboard/OperatorStatusBanner";
import { OperatorQualityPanel } from "@/components/admin/dashboard/OperatorQualityPanel";
import { AttentionPanel } from "@/components/admin/dashboard/AttentionPanel";
import { UsageTrendPanel } from "@/components/admin/dashboard/UsageTrendPanel";
import { AccountHealthPanel } from "@/components/admin/dashboard/AccountHealthPanel";
import { OperatorIntegrationsPanel } from "@/components/admin/dashboard/OperatorIntegrationsPanel";
import { UsageDrillTable } from "@/components/admin/dashboard/UsageDrillTable";
import { OperatorDrillOverlay } from "@/components/admin/dashboard/OperatorDrillOverlay";

// ── Command palette ───────────────────────────────────────────────────────────
const PALETTE_COMMANDS = [
  { id: "open_health", label: "Open Platform Health", hint: "Ops sub-panels" },
  { id: "open_tenants", label: "Go to Tenants", hint: "All tenants list" },
  {
    id: "open_bias",
    label: "Bias guardrails",
    hint: "Platform guardrails editor",
  },
] as const;

type PaletteCommand = (typeof PALETTE_COMMANDS)[number]["id"];

function CommandPalette({
  open,
  onClose,
}: {
  open: boolean;
  onClose: () => void;
}) {
  const navigate = useNavigate();
  if (!open) return null;

  function handleCommand(id: PaletteCommand) {
    onClose();
    switch (id) {
      case "open_health":
        navigate("/platform-health");
        break;
      case "open_tenants":
        navigate("/tenants");
        break;
      case "open_bias":
        navigate("/bias-guardrails");
        break;
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center pt-[12vh]">
      <button
        type="button"
        aria-label="Close palette"
        onClick={onClose}
        className="absolute inset-0 bg-black/55 backdrop-blur-sm"
      />
      <div className="relative w-full max-w-lg overflow-hidden rounded-xl border border-border-strong bg-surface-card shadow-2xl">
        <div className="flex items-center gap-2 border-b border-border-default px-4 py-3">
          <svg
            width="15"
            height="15"
            viewBox="0 0 15 15"
            className="shrink-0 text-text-tertiary"
            aria-hidden="true"
          >
            <circle
              cx="6.5"
              cy="6.5"
              r="4.5"
              stroke="currentColor"
              strokeWidth="1.2"
              fill="none"
            />
            <path
              d="M10 10l3 3"
              stroke="currentColor"
              strokeWidth="1.2"
              strokeLinecap="round"
            />
          </svg>
          <input
            autoFocus
            placeholder="Type a command…"
            className="w-full bg-transparent text-sm text-text-primary outline-none placeholder:text-text-tertiary"
          />
          <kbd className="rounded border border-border-strong bg-surface-canvas px-1.5 py-0.5 font-mono text-[10px] text-text-tertiary">
            ESC
          </kbd>
        </div>
        <div className="max-h-[320px] overflow-y-auto p-2">
          <p className="px-2 py-1 text-[10px] font-bold uppercase tracking-[0.14em] text-text-tertiary">
            Navigate
          </p>
          {PALETTE_COMMANDS.map((c) => (
            <button
              key={c.id}
              type="button"
              onClick={() => handleCommand(c.id)}
              className="flex w-full items-center justify-between rounded-md px-2 py-2 text-left hover:bg-surface-card-hover"
            >
              <span className="text-sm text-text-primary">{c.label}</span>
              <span className="text-2xs text-text-tertiary">{c.hint}</span>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}

// ── Main page ─────────────────────────────────────────────────────────────────
export default function Dashboard(): JSX.Element {
  const [paletteOpen, setPaletteOpen] = useState(false);
  const [drillTenantId, setDrillTenantId] = useState<string | null>(null);
  const [drillTenantName, setDrillTenantName] = useState<string | null>(null);

  // Pre-fetch calibration + account health so the drill overlay accesses them
  // synchronously without a waterfall — data is already in-cache from the panels.
  const calibQ = useSaCalibrationHealth();
  const accountQ = useSaAccountHealth();

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setPaletteOpen((v) => !v);
      }
      if (e.key === "Escape") {
        setPaletteOpen(false);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  function openDrill(tenantId: string, name: string) {
    setDrillTenantId(tenantId);
    setDrillTenantName(name);
  }

  function closeDrill() {
    setDrillTenantId(null);
    setDrillTenantName(null);
  }

  const calibrationForDrill = calibQ.data?.tenants.find(
    (t) => t.tenant_id === drillTenantId,
  );
  const accountForDrill = accountQ.data?.tenants.find(
    (t) => t.tenant_id === drillTenantId,
  );

  return (
    // P0-4 (founder ruling B): constrain the console to the locked 1440px
    // design-system max-width and center it. At 0–1 customers a contained
    // column reads deliberate instead of a half-empty ~1900px sprawl; as
    // tenants/data grow the same grid fills naturally. The residual lower
    // canvas is intentional calm whitespace — we do NOT stretch empty cards
    // to fake-fill the fold.
    <div
      data-testid="page-ready"
      className="mx-auto w-full max-w-[1440px] space-y-3"
    >
      {/* Page title row */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-base font-semibold tracking-tight text-text-emphasis">
            Platform operations
          </h1>
          <p className="text-2xs text-text-tertiary">
            Super-admin · all customers · this period
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Link
            to="/platform-health"
            className="rounded-md border border-border-default px-3 py-1.5 text-2xs font-semibold uppercase tracking-[0.12em] text-text-secondary hover:border-accent-secondary hover:text-text-primary"
          >
            Health →
          </Link>
        </div>
      </div>

      {/* 4-cell KPI strip */}
      <OperatorKpiStrip />

      {/* Worst platform-health signal echoed from PlatformHealth */}
      <OperatorStatusBanner />

      {/* Two-band operator grid that bottoms near the fold (P0-4).
          Band 1: the two daily-work panels — Attention + AI-quality — at equal
          min-height. Band 2: Usage / Account-health / Integrations as 3 equal
          columns with a fixed min-height band. */}
      <div className="space-y-3">
        {/* Band 1 — daily work: Attention + AI quality, equal height */}
        <div className="grid grid-cols-12 gap-3">
          {/* Attention list — the daily work queue, reads first (left) */}
          <div className="col-span-12 flex lg:col-span-5">
            <AttentionPanel onOpenDrill={openDrill} />
          </div>
          {/* Platform AI Quality cluster — only panel earning a full chart */}
          <div className="col-span-12 flex lg:col-span-7">
            <OperatorQualityPanel />
          </div>
        </div>

        {/* Band 2 — portfolio: usage / account health / integrations, 3-up */}
        <div className="grid grid-cols-1 gap-3 lg:grid-cols-3">
          <div className="flex">
            <UsageTrendPanel />
          </div>
          <div className="flex">
            <AccountHealthPanel onOpenDrill={openDrill} />
          </div>
          <div className="flex">
            <OperatorIntegrationsPanel />
          </div>
        </div>

        {/* Per-request cost drill-down — read-only operator cost tool. Full
            width below the portfolio band so the row table has room. */}
        <div className="flex">
          <UsageDrillTable />
        </div>
      </div>

      {/* ⌘K — pinned bottom-right affordance, OUT of grid flow (P0-4) */}
      <button
        type="button"
        onClick={() => setPaletteOpen(true)}
        className="fixed bottom-5 right-6 z-30 flex items-center gap-2 rounded-full border border-border-default bg-surface-card/90 px-3 py-1.5 text-2xs font-medium uppercase tracking-[0.14em] text-text-tertiary shadow-sm backdrop-blur transition hover:border-accent-secondary hover:text-text-secondary"
      >
        <kbd className="rounded border border-border-strong bg-surface-canvas px-1.5 py-0.5 font-mono text-[10px] text-text-secondary">
          ⌘ K
        </kbd>
        <span>command palette</span>
      </button>

      {/* Per-tenant drill overlay */}
      <OperatorDrillOverlay
        tenantId={drillTenantId}
        tenantName={drillTenantName}
        calibrationData={calibrationForDrill}
        accountData={accountForDrill}
        onClose={closeDrill}
      />

      {/* ⌘K command palette */}
      <CommandPalette
        open={paletteOpen}
        onClose={() => setPaletteOpen(false)}
      />
    </div>
  );
}
